import {
  reviewSummary,
  type HsnCode,
  type Product,
  type ProductWithVariants,
  type Variant,
} from "@estelle/domain";

/** A row flattened to the field names its module declares. */
export type Projection = Record<string, unknown>;

/**
 * A product row enriched with the figures FR-CAT-1 wants beside it —
 * variant count, price range, HSN coverage — none of which live on
 * `Product` itself. `listProducts` returns the product alone; its variants
 * are a separate resource, so `ProductList` resolves each row's variants
 * once the page arrives and merges the result into this shape with
 * `buildProductRow` before anything is projected or rendered. That keeps
 * the promise every projection here makes: computed from the row in front
 * of it, nothing else, because the row has already been made whole by the
 * time it gets here.
 */
export interface ProductRow extends Product {
  variantCount: number;
  /** The cheapest and priciest variant's own price string. Null with no variants. */
  priceMin: string | null;
  priceMax: string | null;
  /** Variants of this product with no HSN code. Zero is full coverage. */
  hsnMissingCount: number;
  /** Every distinct HSN code across the variants, in variant order. */
  hsnCodes: string[];
}

/**
 * Fold a product's variants into the figures the list displays.
 *
 * Money is compared, never summed — `priceMin`/`priceMax` keep the winning
 * variant's own decimal string rather than a value rebuilt from a parsed
 * number, so the total a screen shows is always one Shopify actually wrote.
 */
export function buildProductRow(
  product: Product,
  detail: ProductWithVariants | null,
): ProductRow {
  const variants = detail?.variants ?? [];

  let priceMin: string | null = null;
  let priceMax: string | null = null;
  let minValue = Infinity;
  let maxValue = -Infinity;

  for (const variant of variants) {
    const value = Number(variant.price);
    if (Number.isNaN(value)) continue;
    if (value < minValue) {
      minValue = value;
      priceMin = variant.price;
    }
    if (value > maxValue) {
      maxValue = value;
      priceMax = variant.price;
    }
  }

  return {
    ...product,
    variantCount: variants.length,
    priceMin,
    priceMax,
    hsnMissingCount: variants.filter((variant) => variant.hsnCode === null)
      .length,
    hsnCodes: [
      ...new Set(
        variants
          .map((variant) => variant.hsnCode)
          .filter((code): code is string => code !== null),
      ),
    ],
  };
}

export function projectProduct(row: ProductRow): Projection {
  return {
    title: row.title,
    vendor: row.vendor ?? "",
    productType: row.productType ?? "",
    status: row.status,
    tags: row.tags,
    collections: row.collections.map((collection) => collection.title),
    variantCount: row.variantCount,
    hasHsn: row.hsnMissingCount === 0,
    hsnCode: row.hsnCodes.join(", "),
    rating: reviewSummary(row.metafields)?.rating ?? null,
    reviewCount: reviewSummary(row.metafields)?.count ?? null,
  };
}

/**
 * The HSN master, projected as-is.
 *
 * Every field the screen shows already lives on `HsnCode` — the ERP owns
 * this table outright, so there is no mirrored-versus-computed split to
 * work around the way there is for a product or a variant.
 */
export function projectHsnCode(code: HsnCode): Projection {
  return {
    code: code.code,
    description: code.description,
    gstRate: code.gstRate,
    cessRate: code.cessRate,
    productCount: code.productCount ?? 0,
  };
}

/**
 * A variant missing its HSN code, with the product title it needs to be
 * recognised by.
 *
 * `Variant` carries `productId`, not the product's title — the same shape
 * problem `ProductRow` solves above, and the same fix: `MissingHsn` resolves
 * the handful of distinct products a page of missing variants can belong to
 * and merges the title in before this ever runs.
 */
export interface MissingHsnRow extends Variant {
  productTitle: string;
}

export function projectMissingHsnVariant(row: MissingHsnRow): Projection {
  return {
    sku: row.sku ?? "",
    productTitle: row.productTitle,
    variantTitle: row.title,
    price: row.price,
  };
}
